import { useState, useEffect } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Trophy, Loader2, RefreshCw, Pencil, Plus, Trash2, Eye, EyeOff,
  TrendingUp, TrendingDown, Minus
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Switch } from "../ui/switch";

const LEAGUES = [
  { id: "premier_league", name: "EFL Championship", team: "First Team" },
  { id: "wsl", name: "Women's Super League", team: "Women" },
  { id: "pl2", name: "Premier League 2", team: "U21" },
  { id: "u18_premier_league", name: "U18 Premier League", team: "U18" }
];

const TablesAdmin = () => {
  const { token } = useAuth();
  const [tables, setTables] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeLeague, setActiveLeague] = useState("premier_league");
  const [scraperStatus, setScraperStatus] = useState(null);
  const [scraping, setScraping] = useState(false);
  const [sectionEnabled, setSectionEnabled] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const emptyTeam = {
    position: 1,
    team: "",
    played: 0,
    won: 0,
    drawn: 0,
    lost: 0,
    goals_for: 0,
    goals_against: 0,
    goal_difference: 0,
    points: 0,
    form: ""
  };

  useEffect(() => {
    fetchTables();
    fetchScraperStatus();
    fetchSectionStatus();
  }, []);

  const fetchTables = async () => {
    try {
      const response = await axios.get(`${API}/tables`);
      // Convert array to object keyed by league
      const tablesObj = {};
      if (Array.isArray(response.data)) {
        response.data.forEach(table => {
          tablesObj[table.league_name || table.league] = table.standings || [];
        });
      } else {
        Object.assign(tablesObj, response.data);
      }
      setTables(tablesObj);
    } catch (error) {
      console.error("Failed to fetch tables:", error);
      toast.error("Failed to load league tables");
    } finally {
      setLoading(false);
    }
  };

  const fetchScraperStatus = async () => {
    try {
      const response = await axios.get(`${API}/tables/scraper-status`);
      setScraperStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch scraper status");
    }
  };

  const fetchSectionStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/section-toggles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSectionEnabled(response.data.tables_section_enabled !== false);
    } catch (error) {
      console.error("Failed to fetch section status");
    }
  };

  const toggleSection = async () => {
    setToggling(true);
    try {
      const response = await axios.post(`${API}/admin/section-toggle/tables_section_enabled`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSectionEnabled(response.data.tables_section_enabled);
      toast.success(`Tables section ${response.data.tables_section_enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error("Failed to toggle section");
    } finally {
      setToggling(false);
    }
  };

  const runScraper = async () => {
    setScraping(true);
    try {
      const response = await axios.get(`${API}/tables/refresh`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || "Tables refreshed");
      fetchTables();
      fetchScraperStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to refresh tables");
    } finally {
      setScraping(false);
    }
  };

  const saveTeam = async () => {
    try {
      // Calculate goal difference and ensure numbers
      const teamData = {
        ...editingTeam,
        goal_difference: (editingTeam.goals_for || 0) - (editingTeam.goals_against || 0),
        points: (editingTeam.won || 0) * 3 + (editingTeam.drawn || 0)
      };

      await axios.put(`${API}/admin/tables/${activeLeague}/team/${editingTeam.position}`, teamData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Team updated");
      setDialogOpen(false);
      setEditingTeam(null);
      fetchTables();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save team");
    }
  };

  const getCurrentTable = () => {
    const league = LEAGUES.find(l => l.id === activeLeague);
    if (!league) return [];
    
    // Try different keys
    return tables[activeLeague] || 
           tables[league.name] || 
           tables[league.name.toLowerCase().replace(/ /g, '_')] || 
           [];
  };

  const currentTable = getCurrentTable();

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#7C2C3B]" /></div>;
  }

  return (
    <div data-testid="tables-admin">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
          <Trophy className="w-8 h-8 inline mr-2 text-[#7C2C3B]" />
          LEAGUE TABLES ADMIN
        </h1>
      </div>

      {/* Section Visibility Toggle */}
      <div className={`rounded-xl p-4 mb-6 border-2 ${sectionEnabled ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {sectionEnabled ? <Eye className="w-6 h-6 text-green-600" /> : <EyeOff className="w-6 h-6 text-red-600" />}
            <div>
              <h3 className="font-bold text-slate-800">Tables Section Visibility</h3>
              <p className="text-sm text-slate-600">
                {sectionEnabled ? "Section is VISIBLE on the website" : "Section is HIDDEN from the website"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className={`px-3 py-1 rounded-full text-sm font-bold ${sectionEnabled ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
              {sectionEnabled ? "VISIBLE" : "HIDDEN"}
            </span>
            <Switch
              checked={sectionEnabled}
              onCheckedChange={toggleSection}
              disabled={toggling}
              data-testid="toggle-tables-visibility"
            />
          </div>
        </div>
      </div>

      {/* Scraper Control Panel */}
      <div className="bg-white rounded-xl p-4 mb-6 border border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <RefreshCw className="w-5 h-5" /> League Tables Scraper
            </h3>
            <p className="text-sm text-slate-500">
              {scraperStatus?.last_updated 
                ? `Last updated: ${new Date(scraperStatus.last_updated).toLocaleString()}`
                : "Updates every 3 hours automatically"}
            </p>
          </div>
          <Button 
            onClick={runScraper} 
            disabled={scraping}
            className="bg-[#2BAEE5] hover:bg-[#228BB7]"
            data-testid="refresh-tables-btn"
          >
            {scraping ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Refreshing...</>
            ) : (
              <><RefreshCw className="w-4 h-4 mr-2" /> Refresh Now</>
            )}
          </Button>
        </div>
      </div>

      {/* League Tabs */}
      <Tabs value={activeLeague} onValueChange={setActiveLeague} className="space-y-4">
        <TabsList className="grid grid-cols-4 w-full">
          {LEAGUES.map(league => (
            <TabsTrigger key={league.id} value={league.id} className="text-xs sm:text-sm">
              {league.name}
            </TabsTrigger>
          ))}
        </TabsList>

        {LEAGUES.map(league => (
          <TabsContent key={league.id} value={league.id}>
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-100">
                      <TableHead className="w-12 text-center">#</TableHead>
                      <TableHead>Team</TableHead>
                      <TableHead className="w-12 text-center">P</TableHead>
                      <TableHead className="w-12 text-center">W</TableHead>
                      <TableHead className="w-12 text-center">D</TableHead>
                      <TableHead className="w-12 text-center">L</TableHead>
                      <TableHead className="w-16 text-center">GF</TableHead>
                      <TableHead className="w-16 text-center">GA</TableHead>
                      <TableHead className="w-16 text-center">GD</TableHead>
                      <TableHead className="w-16 text-center">Pts</TableHead>
                      <TableHead className="w-24">Form</TableHead>
                      <TableHead className="w-20">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currentTable.map((team, idx) => (
                      <TableRow 
                        key={team.team || idx}
                        className={team.team?.includes("West Ham") ? "bg-[#7C2C3B]/10 font-bold" : ""}
                      >
                        <TableCell className="text-center font-bold">{team.position || idx + 1}</TableCell>
                        <TableCell className="font-medium">{team.team}</TableCell>
                        <TableCell className="text-center">{team.played}</TableCell>
                        <TableCell className="text-center text-green-600">{team.won}</TableCell>
                        <TableCell className="text-center text-slate-500">{team.drawn}</TableCell>
                        <TableCell className="text-center text-red-600">{team.lost}</TableCell>
                        <TableCell className="text-center">{team.goals_for || team.gf}</TableCell>
                        <TableCell className="text-center">{team.goals_against || team.ga}</TableCell>
                        <TableCell className="text-center">
                          <span className={(team.goal_difference || team.gd) > 0 ? "text-green-600" : (team.goal_difference || team.gd) < 0 ? "text-red-600" : ""}>
                            {(team.goal_difference || team.gd) > 0 ? "+" : ""}{team.goal_difference || team.gd}
                          </span>
                        </TableCell>
                        <TableCell className="text-center font-bold text-lg">{team.points}</TableCell>
                        <TableCell>
                          <div className="flex gap-0.5">
                            {(team.form || "").split("").slice(0, 5).map((result, i) => (
                              <span 
                                key={i}
                                className={`w-5 h-5 rounded text-white text-xs flex items-center justify-center ${
                                  result === 'W' ? 'bg-green-500' :
                                  result === 'D' ? 'bg-slate-400' :
                                  result === 'L' ? 'bg-red-500' : 'bg-slate-200'
                                }`}
                              >
                                {result}
                              </span>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 w-7 p-0"
                            onClick={() => { setEditingTeam({...team, position: team.position || idx + 1}); setDialogOpen(true); }}
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {currentTable.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <Trophy className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No standings data available. Click "Refresh Now" to fetch latest data.</p>
                </div>
              )}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* Edit Team Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>EDIT TEAM</DialogTitle>
          </DialogHeader>
          
          {editingTeam && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Position</Label>
                  <Input 
                    type="number"
                    min="1"
                    value={editingTeam.position} 
                    onChange={(e) => setEditingTeam({...editingTeam, position: parseInt(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Team Name</Label>
                  <Input 
                    value={editingTeam.team} 
                    onChange={(e) => setEditingTeam({...editingTeam, team: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <Label>Played</Label>
                  <Input 
                    type="number"
                    min="0"
                    value={editingTeam.played || 0} 
                    onChange={(e) => setEditingTeam({...editingTeam, played: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>Won</Label>
                  <Input 
                    type="number"
                    min="0"
                    value={editingTeam.won || 0} 
                    onChange={(e) => setEditingTeam({...editingTeam, won: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>Drawn</Label>
                  <Input 
                    type="number"
                    min="0"
                    value={editingTeam.drawn || 0} 
                    onChange={(e) => setEditingTeam({...editingTeam, drawn: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>Lost</Label>
                  <Input 
                    type="number"
                    min="0"
                    value={editingTeam.lost || 0} 
                    onChange={(e) => setEditingTeam({...editingTeam, lost: parseInt(e.target.value) || 0})}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Goals For</Label>
                  <Input 
                    type="number"
                    min="0"
                    value={editingTeam.goals_for || 0} 
                    onChange={(e) => setEditingTeam({...editingTeam, goals_for: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>Goals Against</Label>
                  <Input 
                    type="number"
                    min="0"
                    value={editingTeam.goals_against || 0} 
                    onChange={(e) => setEditingTeam({...editingTeam, goals_against: parseInt(e.target.value) || 0})}
                  />
                </div>
              </div>
              
              <div>
                <Label>Form (e.g., WWDLW)</Label>
                <Input 
                  value={editingTeam.form || ""} 
                  onChange={(e) => setEditingTeam({...editingTeam, form: e.target.value.toUpperCase()})}
                  maxLength={10}
                  placeholder="Last 5 results: W=Win, D=Draw, L=Loss"
                />
              </div>
              
              <div className="text-sm text-slate-500 bg-slate-50 p-3 rounded">
                <p><strong>Goal Difference:</strong> {(editingTeam.goals_for || 0) - (editingTeam.goals_against || 0)}</p>
                <p><strong>Points:</strong> {(editingTeam.won || 0) * 3 + (editingTeam.drawn || 0)}</p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveTeam} className="bg-[#7C2C3B] hover:bg-[#63232F]" data-testid="save-team-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TablesAdmin;
